import { useCallback, useEffect, useState } from "react";
import { IconSize } from "../../../classes/enums/ButtonEnum";
import { EventsEnum } from "../../../classes/enums/EventsEnum";
import IconsEnum from "../../../classes/enums/IconsEnum";
import IconButton from "../../../components/iconButton/IconButton";
import "./ValidationHistoryButtons.css";

/**
 * ValidationHistoryButtons component provides undo, redo, and reset functionality
 * for validation operations. The buttons are positioned in the upper left corner
 * and only appear if callbacks are provided.
 *
 * Button enable/disable logic:
 * - Undo: Enabled when at least one action is performed
 * - Redo: Enabled when at least 2 actions are performed AND user clicked undo at least once
 * - Reset: Enabled when at least one action is performed
 */
function ValidationHistoryButtons({
  onUndo,
  onRedo,
  onReset,
  getHistoryState,
  viewId,
  contrastClass = "light",
}) {
  const [buttonStates, setButtonStates] = useState({
    canUndo: false,
    canRedo: false,
    canReset: false,
  });

  // Fetch history state to determine button enable/disable state
  const updateHistoryState = useCallback(async () => {
    if (!getHistoryState) return;

    try {
      const state = await getHistoryState();
      if (state && !state.error) {
        setButtonStates({
          canUndo: state.canUndo || false,
          canRedo: state.canRedo || false,
          canReset: state.canReset || false,
        });
      }
    } catch (error) {}
  }, [getHistoryState]);

  // Update history state on mount and whenever needed
  useEffect(() => {
    updateHistoryState();
  }, [updateHistoryState]);

  // Subscribe to UPDATE_VIEWS events to refresh button states
  useEffect(() => {
    const handleUpdateViews = event => {
      // Only respond to UPDATE_VIEWS events for THIS specific view
      // event.detail contains the viewId from the backend
      if (!viewId) {
        // If no viewId provided, update anyway (backward compatibility)
        updateHistoryState();
        return;
      }

      // Extract viewId from event detail
      // The backend sends viewId as the data in UPDATE_VIEWS
      const eventViewId = Array.isArray(event.detail)
        ? event.detail[0]
        : event.detail;

      // Only update if this event is for our view
      if (eventViewId === viewId) {
        updateHistoryState();
      }
    };

    // Listen for UPDATE_VIEWS events dispatched by RenderingClient
    window.addEventListener(EventsEnum.UPDATE_VIEWS, handleUpdateViews);

    return () => {
      window.removeEventListener(EventsEnum.UPDATE_VIEWS, handleUpdateViews);
    };
  }, [updateHistoryState, viewId]);

  const handleUndo = useCallback(async () => {
    if (!onUndo || !buttonStates.canUndo) return;

    try {
      await onUndo();
      // Update state after successful undo
      await updateHistoryState();
    } catch (error) {}
  }, [onUndo, buttonStates.canUndo, updateHistoryState]);

  const handleRedo = useCallback(async () => {
    if (!onRedo || !buttonStates.canRedo) return;

    try {
      await onRedo();
      // Update state after successful redo
      await updateHistoryState();
    } catch (error) {}
  }, [onRedo, buttonStates.canRedo, updateHistoryState]);

  const handleReset = useCallback(async () => {
    if (!onReset || !buttonStates.canReset) return;

    try {
      await onReset();
      // Update state after successful reset
      await updateHistoryState();
    } catch (error) {}
  }, [onReset, buttonStates.canReset, updateHistoryState]);

  // Don't render if no callbacks are provided
  if (!onUndo && !onRedo && !onReset) {
    return null;
  }

  return (
    <div className="validation-history-buttons">
      {onUndo && (
        <IconButton
          icon={IconsEnum.UNDO}
          onClick={handleUndo}
          iconSize={IconSize.MEDIUM}
          isCircle={true}
          noBgColor={true}
          title="Undo"
          className={`validation-history-icon validation-history-icon-${contrastClass} ${
            !buttonStates.canUndo ? "validation-history-icon-disabled" : ""
          }`}
        />
      )}
      {onRedo && (
        <IconButton
          icon={IconsEnum.REDO}
          onClick={handleRedo}
          iconSize={IconSize.MEDIUM}
          isCircle={true}
          noBgColor={true}
          title="Redo"
          className={`validation-history-icon validation-history-icon-${contrastClass} ${
            !buttonStates.canRedo ? "validation-history-icon-disabled" : ""
          }`}
        />
      )}
      {onReset && (
        <IconButton
          icon={IconsEnum.RESET}
          onClick={handleReset}
          iconSize={IconSize.MEDIUM}
          isCircle={true}
          noBgColor={true}
          title="Reset"
          className={`validation-history-icon validation-history-icon-${contrastClass} ${
            !buttonStates.canReset ? "validation-history-icon-disabled" : ""
          }`}
        />
      )}
    </div>
  );
}

export default ValidationHistoryButtons;
